import axios from 'axios';
import config from '../config';

// Set default base URL only if provided (for proxy mode, leave it empty)
if (config.API_URL) {
  axios.defaults.baseURL = config.API_URL.replace(/\/$/, '');
}

// Hoisted up front so the request interceptor below can read it
// without temporal-dead-zone risk. Cleared on the first authenticated
// request after a successful login (see interceptor).
let _logoutInFlight = false;

// Add auth token + active company context to requests, AND short-circuit
// any authenticated request when we have no token. The short-circuit is
// the load-bearing fix for the "can't log back in after the backend
// restarts" class of bug:
//
//   When the backend restarts, the user's JWT becomes invalid mid-page.
//   The Dashboard's pollers (live-activity every 8s, snapshot every
//   10s, the LiveActivityBeacon every 30s) keep firing. Each one
//   carries the stale token, each gets 401, each cascades into
//   forceLogout → window.location.href = '/login'. But the polls keep
//   queuing requests until React unmounts the Dashboard, and that
//   backlog blocks the user's login POST behind it. Login times out
//   (499 in nginx) until the backend finally drains the queue.
//
//   Cancelling at the interceptor level kills zombie polls AT THE
//   SOURCE so the network never sees them and the user's login POST
//   has the connection pool to itself. Combined with stopTokenRefresh
//   in forceLogout (below), this turns the 60+-second post-restart
//   stall into a clean logout-and-relogin flow.
// Endpoints that can legitimately be hit without an auth token.
// Authenticated polling endpoints (jobs, dashboard, companies-context,
// etc.) are NOT here — they're the ones we want to short-circuit when
// the user has been logged out, so the user's next login POST isn't
// queued behind a backlog of doomed authenticated requests.
function isPublicEndpoint(url) {
  if (typeof url !== 'string') return false;
  return (
    url.includes('/api/auth/')               // login, refresh, register, signup-allowed
    || url.includes('/api/d/')               // public download landing
    || url.includes('/api/watch/')           // public watch player events
    || url.includes('/api/request/')         // public venue KDM request form
    || url.includes('/api/users/invitations/')  // invite preview (Register page)
  );
}

// Endpoints that DON'T require a tenant context (no X-Company-Id).
// Hitting one of these without activeCompanyId is fine. Everything
// else is tenant-scoped — firing it without an X-Company-Id makes the
// backend fall back to unscoped queries (e.g. /mnt/source instead of
// /mnt/source/<slug>) which returns empty / wrong data.
//
// The Sources-page race that motivated this discriminator: the page
// fired fetchAvailableFiles on mount before the company-context
// handshake completed. Backend returned `{files: [], source_path:
// "/mnt/source"}` (40 bytes), the page rendered "no sources." Two
// minutes later the context loaded, the page refetched and got 6
// files. Operator correctly flagged this as a real race, not a
// transient hiccup.
function isTenantAgnosticEndpoint(url) {
  if (typeof url !== 'string') return false;
  return (
    isPublicEndpoint(url)
    || url.includes('/api/users/me')
    || url.includes('/api/users/profile/me')
    || url.includes('/api/companies-context/')
    || url.includes('/api/operator/')
    // Physical-delivery endpoints: operator-side ops have no company
    // context (you're acting as HHMG, not as a tenant). The backend
    // handles both shapes — superadmin without X-Company-Id sees all
    // rows; company members include the header normally. Without this
    // entry, the request interceptor cancels the GET before it leaves
    // the browser and the Physical Drives page shows empty data.
    || url.includes('/api/physical-delivery/')
    // Operator-only configuration endpoints. They're guarded by
    // require_superadmin server-side and have no tenant context — the
    // operator is acting as HHMG, not as a member of any company. Pre-
    // this-entry, hitting Save on /operator/email got canceled by the
    // tenant-context gate (axios.Cancel has no response.data.detail,
    // so the UI fell through to "Failed to save" with no clue what
    // happened). Same class of bug applies to every other operator
    // surface, so we whitelist the family.
    || url.includes('/api/email/')
    || url.includes('/api/email-templates/')
    || url.includes('/api/storage/')
    || url.includes('/api/settings/')
    || url.includes('/api/encoders/')
    || url.includes('/api/dcp-nodes/')
    || url.includes('/api/encoder-updates/')
    // FedEx integration (v10.18.0+): credentials, shipper address, and
    // test-connection all live in system_settings — global, not
    // per-tenant. Without this entry, an operator without an active
    // company context (or who hasn't yet selected one) gets their PUT
    // canceled with "no active company yet — request canceled by
    // request interceptor" before it leaves the browser.
    || url.includes('/api/fedex/')
    // Admin/diagnostic surfaces — system status, etc.
    || url.includes('/api/admin/')
    // Bug reports (v10.20.13). The list endpoint is site-wide
    // (require_superadmin, no company filter on the server), so the
    // operator should see every tenant's reports. Pre-this-entry,
    // hitting /operator/bug-reports without an active company picked
    // got the GET canceled by the tenant-context gate and the page
    // showed empty — making it look like there were no bugs when
    // there were dozens.
    || url.includes('/api/bug-reports')
  );
}

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('userToken');
    const url = config.url || '';
    if (!token && !isPublicEndpoint(url) && !config._skipAuthGate) {
      // Synthetic cancel — axios treats this as a request failure and
      // the call never hits the network. Callers that handle errors
      // see a Cancel; pollers swallow it silently.
      const err = new axios.Cancel('no auth token — request canceled by request interceptor');
      throw err;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // A new authenticated request implies a valid session: clear the
      // logout-debounce so a future session-loss can fire a fresh
      // forceLogout. Without this, after one logout+login cycle the
      // flag stays true and subsequent 401s would silently no-op.
      if (_logoutInFlight) _logoutInFlight = false;
    }
    const companyId = localStorage.getItem('activeCompanyId');
    if (companyId) {
      config.headers['X-Company-Id'] = companyId;
    } else if (token && !isTenantAgnosticEndpoint(url) && !config._skipAuthGate) {
      // Authenticated but no active tenant context yet. A tenant-scoped
      // request without X-Company-Id makes the backend return unscoped
      // garbage (Sources page got "no files" for ~2 minutes during the
      // mount race that triggered this fix). Cancel here; the caller
      // re-fires once activeCompanyId arrives via the Redux update.
      const err = new axios.Cancel('no active company yet — request canceled by request interceptor');
      throw err;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Activity-based token refresh.
//
// The backend issues a single JWT with a moderate TTL. There is no
// separate refresh token, so once a JWT actually expires we cannot
// recover it — we MUST refresh BEFORE expiry. The previous design
// failed users in three ways and we fix all three:
//
//   1. Any 401 immediately force-logged out, with no retry. A single
//      transient 401 (backend restart, race window during refresh)
//      kicked the user out mid-task. We now attempt a refresh + retry
//      first; logout only if refresh ALSO fails or the original 401
//      came FROM the refresh endpoint itself.
//
//   2. setInterval is throttled to once-per-minute in background tabs
//      and pauses entirely when a laptop sleeps. With a 30-min TTL and
//      a 15-min refresh, falling asleep at the laptop guaranteed a
//      logout on next interaction. We now also refresh on
//      `visibilitychange` and `focus`, catching the "back from sleep"
//      case before the next API call hits.
//
//   3. The IDLE_TIMEOUT check considered any API call as "user
//      activity" — but Library/Queue pages poll every 3 s, so the
//      idle timer literally never fired. We now only count genuine
//      user input (mouse, key, scroll, touch, focus) as activity.

const REFRESH_INTERVAL = 12 * 60 * 1000;          // 12 min — well inside the 30-min JWT TTL
const VISIBILITY_REFRESH_THRESHOLD = 5 * 60 * 1000; // refresh on focus if last refresh was >5 min ago
const IDLE_TIMEOUT = 60 * 60 * 1000;              // 60 min of NO user input = expire

let refreshTimer = null;
let lastUserActivity = Date.now();
let lastRefreshAt = Date.now();
let refreshInFlight = null;  // Promise dedup so concurrent failures don't all hit /refresh

function isRefreshEndpoint(url) {
  // The refresh endpoint failing means the user's token is dead and
  // we should force-logout. Distinct from the login endpoint:
  // someone typing wrong creds at /login has no session to clear.
  return typeof url === 'string' && url.includes('/api/auth/refresh');
}

function isLoginEndpoint(url) {
  // 401 from these = bad credentials. Bubble up to the userLogin
  // thunk's catch, never trigger forceLogout. v10.21.24: pre-fix
  // the login endpoint shared the forceLogout codepath with the
  // refresh endpoint, which raced the Redux reject reducer's
  // state.error assignment — sometimes the "Incorrect username or
  // password" alert rendered, sometimes the localStorage clear +
  // persist-cache reset cascaded and the alert never appeared.
  // Second security review called this out as inconsistent error
  // feedback.
  return typeof url === 'string' && (
    url.includes('/api/auth/login') ||
    url.includes('/api/auth/token')
  );
}

function forceLogout(reason) {
  // Debounce: 50 simultaneous 401s shouldn't trigger 50 logout
  // navigations. Once we've kicked off the logout-and-redirect, every
  // subsequent caller is a no-op.
  if (_logoutInFlight) return;
  _logoutInFlight = true;
  console.warn(`[auth] forced logout: ${reason}`);
  // Stop the refresh timer FIRST — otherwise it can fire one last
  // performRefresh against the about-to-be-cleared token between this
  // line and the navigation, racing with the user's next login.
  stopTokenRefresh();
  localStorage.removeItem('userToken');
  localStorage.removeItem('user');
  // Also clear tenant context — pre-v10.17.14 we left `activeCompanyId`
  // and `activeProduct` behind on a force-logout, so the next login on
  // the same page picked them back up and the dashboard tried to render
  // for the wrong company. The axios interceptor would then cancel the
  // first tenant-scoped request with "no active company yet …" because
  // localStorage had been cleared between the Redux read and the
  // request fire. Clearing them here makes login flows deterministic:
  // every fresh login starts with no tenant context regardless of what
  // happened to the previous session.
  localStorage.removeItem('activeCompanyId');
  localStorage.removeItem('activeProduct');
  // Wipe the SWR persist cache so the post-relogin session doesn't
  // briefly render the previous user's cached data. Lazy import to
  // avoid pulling the Redux store into the axios bundle on first
  // load (interceptors run before the store exists in HMR).
  try {
    import('../store/persist').then((m) => m.clearPersist?.());
  } catch (_e) { /* ignore */ }
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function performRefresh() {
  // Single in-flight refresh — multiple 401s in quick succession all
  // await the same promise instead of fanning out into a refresh storm.
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const token = localStorage.getItem('userToken');
    if (!token) throw new Error('no token to refresh');
    try {
      const { data } = await axios.post('/api/auth/refresh', null, {
        // Bypass our own response interceptor's 401 logout — if the
        // refresh itself fails, performRefresh's caller decides what
        // to do, never an infinite logout loop.
        _skipAuthRetry: true,
      });
      if (data?.access_token) {
        localStorage.setItem('userToken', data.access_token);
        lastRefreshAt = Date.now();
        return data.access_token;
      }
      throw new Error('refresh response missing access_token');
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// Idle-driven scheduled refresh. Required only as a fallback for
// long-lived foreground tabs; the focus/visibility hook below handles
// the "back from sleep" case better.
async function scheduledRefresh() {
  if (!localStorage.getItem('userToken')) return;
  if (Date.now() - lastUserActivity > IDLE_TIMEOUT) {
    forceLogout('idle for >60 min');
    return;
  }
  try { await performRefresh(); } catch (_) { /* surfaces via 401 path */ }
}

function startTokenRefresh() {
  stopTokenRefresh();
  refreshTimer = setInterval(scheduledRefresh, REFRESH_INTERVAL);
}

function stopTokenRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

function onUserActivity() {
  lastUserActivity = Date.now();
}

// v10.21.37: long-running uploads count as user activity.
//
// Pre-fix: the idle-logout was driven only by pointer/key/scroll/touch
// events. A user uploading a 50 GB master would start the upload, walk
// away or just watch the progress bar, and after 60 minutes the
// scheduled refresh would call forceLogout('idle for >60 min') and
// boot them to /login. They reported getting kicked out twice
// "halfway through" a 3-hour upload because they weren't moving the
// mouse.
//
// The upload manager calls this on every chunk-progress callback (so
// roughly every few seconds while uploading), keeping the idle timer
// fresh for the full duration of any in-flight transfer. Importantly
// it does NOT bypass the refresh logic — JWT auto-refresh still runs
// on schedule, just without the idle-logout firing in the middle.
export function noteUploadActivity() {
  lastUserActivity = Date.now();
}

// Track GENUINE user activity — pointer/key/scroll/focus only, not
// background API polls.
// Idempotent install: if axios.js is hot-reloaded (dev) or imported
// twice in some bundler edge case, we don't want duplicate listeners
// firing. The window flag pins the install state at module level.
const _ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'focus'];
if (typeof window !== 'undefined' && !window.__hhmgActivityListenersInstalled) {
  window.__hhmgActivityListenersInstalled = true;
  _ACTIVITY_EVENTS.forEach(evt => {
    document.addEventListener(evt, onUserActivity, { passive: true });
  });
}

// On tab focus/visibility regain, proactively refresh if it's been a
// while. Catches the laptop-sleep case before the next request 401s.
async function maybeRefreshOnFocus() {
  if (!localStorage.getItem('userToken')) return;
  if (document.visibilityState !== 'visible') return;
  if (Date.now() - lastRefreshAt < VISIBILITY_REFRESH_THRESHOLD) return;
  if (Date.now() - lastUserActivity > IDLE_TIMEOUT) {
    forceLogout('visible after long idle');
    return;
  }
  try { await performRefresh(); } catch (_) { /* 401 path will pick up */ }
}
// Same idempotent guard as the activity-event install above — guards
// against duplicate registrations under HMR or accidental double-import.
if (typeof window !== 'undefined' && !window.__hhmgRefreshListenersInstalled) {
  window.__hhmgRefreshListenersInstalled = true;
  document.addEventListener('visibilitychange', maybeRefreshOnFocus);
  window.addEventListener('focus', maybeRefreshOnFocus);
}

// Response interceptor: on 401, try to refresh + retry the request
// once. Only force-logout if the refresh itself fails or this was
// already a retry attempt. Avoids the "single transient 401 = boot
// to /login" trap.
//
// Also normalizes the error response: if FastAPI / Pydantic v2 returns
// an array `detail` (the validation-error shape), we flatten it into a
// `detailString` field on response.data so call sites that pass
// `error.response.data.detail` straight into a React child don't
// crash with React #31. The original `detail` is preserved untouched
// for code that wants the structured payload.
import { formatApiError } from './errorMessage';

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Normalize first so the caller — including auth-retry below —
    // always sees a stringifiable error.
    if (error?.response?.data?.detail !== undefined) {
      error.response.data.detailString = formatApiError(error);
    }

    const status = error.response?.status;
    const cfg = error.config || {};
    if (status !== 401) return Promise.reject(error);
    // 401 from /api/auth/token (login) is just wrong credentials —
    // bubble up to the userLogin thunk's catch, which sets
    // state.error so the Alert renders. v10.21.24 fix.
    if (isLoginEndpoint(cfg.url || '')) {
      return Promise.reject(error);
    }
    // 401 from /api/auth/refresh: the user's token is dead. Clear
    // session + redirect to /login.
    if (isRefreshEndpoint(cfg.url || '')) {
      forceLogout('refresh endpoint 401');
      return Promise.reject(error);
    }
    // Already retried once — give up.
    if (cfg._authRetried) {
      forceLogout('401 after refresh+retry');
      return Promise.reject(error);
    }
    if (cfg._skipAuthRetry) {
      // Caller opted out of the retry path.
      return Promise.reject(error);
    }
    // Try to refresh, then retry the original request once.
    try {
      const newToken = await performRefresh();
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${newToken}`;
      cfg._authRetried = true;
      return axios(cfg);
    } catch (_refreshErr) {
      forceLogout('refresh failed after 401');
      return Promise.reject(error);
    }
  }
);

// Start refresh if we already have a token
if (localStorage.getItem('userToken')) {
  startTokenRefresh();
}

// Listen for storage changes (login/logout in other tabs)
window.addEventListener('storage', (e) => {
  if (e.key === 'userToken') {
    if (e.newValue) {
      startTokenRefresh();
    } else {
      stopTokenRefresh();
    }
  }
});

export { startTokenRefresh, stopTokenRefresh };
export default axios;
